import { createAsyncThunk } from "@reduxjs/toolkit";

import { RootState } from "../store";
import { SessionData } from "../../interfaces/SessionData";
import { DashboardState } from "../../interfaces/DashboardState";
import { sessionConfigLoaded } from "./dashboardSlice";
import { setConversation } from "./chatSlice";
import { apiClient } from "../../lib/api";
import { ArboristNodeData } from "../../lib/utils";

export const saveSessionData = createAsyncThunk(
  "session/saveSessionData",
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { logId, layout, panes, annotations } = state.dashboardReducer;
    const { conversation } = state.chatReducer;

    try {
      // Sanitize annotations: round any timestamp-like fields to integers
      const sanitizedAnnotations: NonNullable<DashboardState["annotations"]> =
        {};
      if (annotations) {
        for (const [plotId, list] of Object.entries(annotations)) {
          if (!list) continue;
          sanitizedAnnotations[plotId] = list.map((a: any) => {
            if (a.type === "interval") {
              return {
                ...a,
                start: Math.round(a.start),
                end: Math.round(a.end),
              };
            }
            if (a.type === "point") {
              return {
                ...a,
                timestamp: Math.round(a.timestamp),
              };
            }
            return a;
          }) as any;
        }
      }

      const sessionData: SessionData = {
        layout,
        conversation,
        panes: panes,
        // dataSources omitted intentionally; they are derived from panes and rehydrated on load
        annotations: sanitizedAnnotations,
      };
      const response = await apiClient.post(
        `/logs/sessions/${logId}/state`,
        sessionData,
      );
      return response.data;
    } catch (error) {
      console.error("Error saving session data:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  },
);

interface fetchLogMetadataProps {
  sessionId: string;
}

interface fetchSessionProps {
  token: string;
  sessionId: string;
  startTime: number;
  endTime: number;
}

export const fetchLogMetadata = createAsyncThunk(
  "dashboard/fetchLogMetadata",
  async ({ sessionId }: fetchLogMetadataProps) => {
    const response = await apiClient.get(`/logs/${sessionId}/metadata`);
    const data = response.data;
    const startTime = new Date(data.startTimestamp).getTime();
    const endTime = new Date(data.endTimestamp).getTime();
    return {
      isIncidentView: data.isIncidentView,
      incidentMemberSessions: data.incidentMemberSessions,
      sessionId: sessionId,
      name: data.name,
      startTime,
      endTime,
    };
  },
);

// New: fetch topics for a specific (source) session on demand
export const fetchTopicsForSession = createAsyncThunk(
  "dashboard/fetchTopicsForSession",
  async ({ sessionId }: { sessionId: string }) => {
    const resp = await apiClient.get(`/logs/sessions/${sessionId}/topics`);
    return resp.data as ArboristNodeData[]; // ArboristNodeData[]
  },
);

export const fetchSession = createAsyncThunk<
  {
    dataSources: DashboardState["dataSources"];
    annotations?: DashboardState["annotations"];
  },
  Omit<fetchSessionProps, "token">
>(
  "dashboard/fetchSession",
  async (
    { sessionId, startTime, endTime }: Omit<fetchSessionProps, "token">,
    { dispatch },
  ) => {
    const response = await apiClient.get<SessionData>(
      `/logs/sessions/${sessionId}/state`,
    );
    const data = response.data;
    if (data.conversation === undefined) {
      data.conversation = [];
    }
    const sessionConfig = {
      layout: data.layout || "Pane 1",
      panes: data.panes || { "Pane 1": {} },
      annotations: data.annotations || {},
    };
    dispatch(sessionConfigLoaded(sessionConfig));
    dispatch(setConversation(data.conversation));

    // Reconstruct dataSources from panes if missing (lean mode) or hydrate existing
    let dataSources: DashboardState["dataSources"] = data.dataSources || {};

    // Build a base map from panes to ensure every plotId has a DataSource entry
    const reconstructed: DashboardState["dataSources"] = { ...dataSources };
    for (const [paneId, pane] of Object.entries(data.panes)) {
      for (const plotId of Object.keys(pane)) {
        if (reconstructed[plotId]) {
          // Ensure subscriber is present
          if (!reconstructed[plotId].subscribers.includes(paneId)) {
            reconstructed[plotId].subscribers.push(paneId);
          }
          continue;
        }
        // Determine key format
        if (plotId.includes(".")) {
          // Could be new format (session.topic.field) or legacy (topic.field)
          const firstDot = plotId.indexOf(".");
          if (firstDot > 0) {
            // New format: <sourceSessionId>.<topic>.<field>
            // Field may contain dots (e.g. pose.position.x), so split topic/field on the
            // first dot after sourceSessionId.
            const sourceSessionId = plotId.slice(0, firstDot);
            const rest = plotId.slice(firstDot + 1);
            const topicFieldDot = rest.indexOf(".");

            if (topicFieldDot > 0) {
              const topicName = rest.slice(0, topicFieldDot);
              const fieldName = rest.slice(topicFieldDot + 1);
              reconstructed[plotId] = {
                topic: topicName,
                field: fieldName,
                data: [],
                timestamps: [],
                subscribers: [paneId],
                sourceSessionId,
              };
              continue;
            }
          }

          const parts = plotId.split(".");
          if (parts.length === 2) {
            // Legacy format: topic.field
            const [topicName, fieldName] = parts;
            reconstructed[plotId] = {
              topic: topicName,
              field: fieldName,
              data: [],
              timestamps: [],
              subscribers: [paneId],
              sourceSessionId: sessionId, // fallback to current session
            };
          } else {
            console.warn("Unrecognized dot key format:", plotId);
          }
        } else if (plotId.includes("/")) {
          // Backward-compat for interim slash format: source/topic/field
          const [sourceSessionId, topicName, fieldName] = plotId.split("/");
          reconstructed[plotId] = {
            topic: topicName,
            field: fieldName,
            data: [],
            timestamps: [],
            subscribers: [paneId],
            sourceSessionId,
          };
        } else {
          // Unknown format; skip
          console.warn("Unrecognized plotId format:", plotId);
        }
      }
    }

    dataSources = reconstructed;

    // Prepare queries for any dataSources lacking data
    const queries: Array<{
      topicName: string;
      fieldName: string;
      sessionId: string;
    }> = [];
    for (const plotId of Object.keys(dataSources)) {
      const ds = dataSources[plotId];
      if (!ds || !ds.data || ds.data.length === 0) {
        const topicName = ds.topic;
        const fieldName = ds.field;
        const source = ds.sourceSessionId || sessionId;
        queries.push({ topicName, fieldName, sessionId: String(source) });
      }
    }

    if (queries.length > 0) {
      const request = {
        sessionId, // context session (kept for API shape)
        startTimestamp: startTime,
        endTimestamp: endTime,
        queries,
      };
      const multiResp = await apiClient.post("/logs/query/multi-data", request);
      const responseData = multiResp.data as Record<
        string,
        { timestamps: number[]; values: any[] }
      >;

      // Map back: server keys are `${topicName}.${fieldName}`
      for (const plotId of Object.keys(dataSources)) {
        const ds = dataSources[plotId];
        const legacyKey = `${ds.topic}.${ds.field}`;
        if (responseData[legacyKey]) {
          ds.data = responseData[legacyKey].values;
          ds.timestamps = responseData[legacyKey].timestamps;
        }
      }
    }

    return { dataSources, annotations: data.annotations || {} };
  },
);
